/**
 * @module domain/cart
 * @description Pure donation-cart reducer (brief §4.6): one line per cause,
 * editable amount, optional note, running total.
 */
import type { CartLine } from "./types";

/** Smallest gift accepted (whole dollars; matches Neon's whole-dollar presets). */
export const MIN_GIFT_USD: number = 1;

/** Cart actions. */
export type CartAction =
    | { type: "add"; causeId: string; amountUsd: number }
    /** Send Love Live: one Shot ADDS its arrow value to the cause's line (Quiver). */
    | { type: "increment"; causeId: string; amountUsd: number }
    | { type: "setAmount"; causeId: string; amountUsd: number }
    | { type: "setNote"; causeId: string; note: string }
    | { type: "remove"; causeId: string }
    | { type: "clear" };

/** @returns a whole-dollar amount ≥ MIN_GIFT_USD (invalid input → MIN_GIFT_USD). */
export function normalizeAmount(amountUsd: number): number {
    if (!Number.isFinite(amountUsd)) return MIN_GIFT_USD;
    return Math.max(MIN_GIFT_USD, Math.round(amountUsd));
}

/**
 * Cart reducer. Adding a cause already in the cart REPLACES its amount
 * (the donor re-chose it) rather than adding a second line, so one cause
 * never appears twice in a checkout.
 */
export function cartReducer(lines: readonly CartLine[], action: CartAction): CartLine[] {
    switch (action.type) {
        case "add": {
            const amountUsd: number = normalizeAmount(action.amountUsd);
            const exists: boolean = lines.some((l: CartLine) => l.causeId === action.causeId);
            return exists
                ? lines.map((l: CartLine) => (l.causeId === action.causeId ? { ...l, amountUsd } : l))
                : [...lines, { causeId: action.causeId, amountUsd, note: "" }];
        }
        case "increment": {
            const exists: boolean = lines.some((l: CartLine) => l.causeId === action.causeId);
            return exists
                ? lines.map((l: CartLine) => (l.causeId === action.causeId ? { ...l, amountUsd: normalizeAmount(l.amountUsd + action.amountUsd) } : l))
                : [...lines, { causeId: action.causeId, amountUsd: normalizeAmount(action.amountUsd), note: "" }];
        }
        case "setAmount":
            return lines.map((l: CartLine) => (l.causeId === action.causeId ? { ...l, amountUsd: normalizeAmount(action.amountUsd) } : l));
        case "setNote":
            return lines.map((l: CartLine) => (l.causeId === action.causeId ? { ...l, note: action.note.slice(0, 280) } : l));
        case "remove":
            return lines.filter((l: CartLine) => l.causeId !== action.causeId);
        case "clear":
            return [];
    }
}

/** @returns the cart total in whole USD. */
export function cartTotal(lines: readonly CartLine[]): number {
    return lines.reduce((sum: number, l: CartLine) => sum + l.amountUsd, 0);
}
